
import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { profileApi } from "@/lib/api/auth";
import { useUserStore } from "@/stores/StoreProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuth } from "@/contexts/AuthContext";
import { DeleteAccountDialog } from "@/components/settings/DeleteAccountDialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { SettingsFormSkeleton } from "@/components/ui/skeletons";

function ProfilePage() {
  usePageTitle('Profile');
  const userStore = useUserStore();
  const queryClient = useQueryClient();
  const { currentOrg, userId } = useAuth();
  const isOwner = currentOrg?.role === "Owner";
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [savedEmail, setSavedEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingEmail, setIsSavingEmail] = useState(false);

  useEffect(() => {
    profileApi.get().then((profile) => {
      setFirstName(profile.firstName ?? "");
      setLastName(profile.lastName ?? "");
      setEmail(profile.email ?? "");
      setSavedEmail(profile.email ?? "");
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  }, []);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setIsSaving(true);
    try {
      const updated = await profileApi.update({
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
      });
      // Update the in-memory UserStore so timeline shows the new name immediately
      userStore.loadFromRecords([{
        id: updated.id,
        email: updated.email,
        firstName: updated.firstName ?? null,
        lastName: updated.lastName ?? null,
        displayName: updated.displayName,
      }]);
      // Persist the new JWT (includes updated ClaimTypes.Name) so future audit logs
      // store the full name instead of the email address
      if (updated.token) {
        localStorage.setItem('token', updated.token);
      }
      toast.success("Profile updated. Your name now appears in activity timelines.");
    } catch {
      toast.error("Failed to update profile.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSaveEmail() {
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error("Email cannot be empty.");
      return;
    }
    setIsSavingEmail(true);
    try {
      const result = await profileApi.changeEmail(trimmed);
      setSavedEmail(result.email);
      setEmail(result.email);
      if (result.token) localStorage.setItem('token', result.token);

      // Reflect the new address locally right away instead of waiting for the
      // sync delta round-trip (owner columns read UserStore directly).
      if (userId) {
        const existing = userStore.getById(userId);
        const firstName = existing?.firstName ?? null;
        const lastName = existing?.lastName ?? null;
        const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
        userStore.loadFromRecords([{
          id: userId,
          email: result.email,
          firstName,
          lastName,
          displayName: fullName || result.email,
        }]);
      }
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] });

      toast.success("Email updated.");
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to update email.";
      toast.error(message);
      // Revert the input to the last saved value
      setEmail(savedEmail);
    } finally {
      setIsSavingEmail(false);
    }
  }

  const emailChanged = email.trim() !== savedEmail;

  if (isLoading) {
    return <SettingsFormSkeleton rows={2} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Profile"
        askAi
        description="Your name appears in activity timelines and audit logs."
        hideTitleOnMobile={false}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Email address</CardTitle>
          <CardDescription>Change the email you use to sign in.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
            {emailChanged && (
              <Button onClick={handleSaveEmail} disabled={isSavingEmail}>
                {isSavingEmail && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save email
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Personal information</CardTitle>
          <CardDescription>Update your display name shown across the app.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  autoComplete="given-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  autoComplete="family-name"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={isSaving} aria-describedby="profile-save-status">
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                Save changes
              </Button>
              <span id="profile-save-status" role="status" aria-live="polite" className="text-sm text-muted-foreground">
                {isSaving ? "Saving…" : ""}
              </span>
            </div>
          </form>
        </CardContent>
      </Card>

      {isOwner && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6">
          <h2 className="text-sm font-semibold text-destructive">Danger Zone</h2>
          <div className="mt-4 flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Delete Account</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Permanently delete your account. This cannot be undone.
              </p>
            </div>
            <DeleteAccountDialog />
          </div>
        </div>
      )}
    </div>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/settings/profile')({ component: ProfilePage });
